import { HD, KeyDeriver, Mnemonic, PrivateKey } from "@bsv/sdk";
import { isSigmaSeedBackup, type SigmaSeedBackup } from "bitcoin-backup";
import { bapIdFromAddress } from "bsv-bap";

/** Version 1 fixes peer derivation and the empty BIP-39 passphrase. */
export type SeedBackup = SigmaSeedBackup;
const ORDER = BigInt(
	"0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141"
);
const MAX_INDEX = 0x7f_ff_ff_ff;
const LENGTHS = [16, 20, 24, 28, 32];
const WHITESPACE = /\s+/;
function assertIndex(index: number): void {
	if (!Number.isInteger(index) || index < 0 || index > MAX_INDEX) {
		throw new Error("Profile index must be an integer from 0 to 2147483647");
	}
}
function mnemonicData(words: string, passphrase = "") {
	if (passphrase !== "") {
		throw new Error("Seed backup version 1 does not support a passphrase");
	}
	if (typeof words !== "string") {
		throw new Error("Recovery words are required");
	}
	const canonical = words
		.normalize("NFKD")
		.trim()
		.toLowerCase()
		.split(WHITESPACE)
		.join(" ");
	const mnemonic = Mnemonic.fromString(canonical);
	const entropy = mnemonic.toEntropy();
	if (!LENGTHS.includes(entropy.length)) {
		throw new Error("Unsupported entropy length");
	}
	const scalar = BigInt(
		`0x${entropy.map((byte) => byte.toString(16).padStart(2, "0")).join("")}`
	);
	if (scalar === BigInt(0) || scalar >= ORDER) {
		throw new Error("Entropy is outside the secp256k1 scalar range");
	}
	return { mnemonic, entropy };
}
/** Receives recovery authority; returns only the selected profile's operational authority. */
export function deriveSeedProfile(
	words: string,
	index: number,
	passphrase = ""
) {
	assertIndex(index);
	const { mnemonic } = mnemonicData(words, passphrase);
	return deriveProfileFromHD(HD.fromSeed(mnemonic.toSeed("")), index);
}
function deriveProfileFromHD(hd: HD, index: number) {
	const path = `m/0'/${index}'`;
	const accountKey = hd.derive(path).privKey;
	const identityAddress = new KeyDeriver(accountKey)
		.derivePublicKey([1, "sigma"], "identity-0", "self", true)
		.toAddress();
	return {
		index,
		path,
		accountWif: accountKey.toWif(),
		accountPublicKey: accountKey.toPublicKey().toString(),
		identityAddress,
		bapId: bapIdFromAddress(identityAddress),
	};
}
export function createSeedBackup(
	words: string,
	createdAt: number,
	label?: string,
	passphrase = ""
): SeedBackup {
	const { mnemonic } = mnemonicData(words, passphrase);
	const profile = deriveSeedProfile(mnemonic.toString(), 0);
	const backup: SeedBackup = {
		format: "sigma-seed",
		version: 1,
		mnemonic: mnemonic.toString(),
		profiles: [{ index: 0, bapId: profile.bapId }],
		nextProfileIndex: 1,
		createdAt,
		...(label === undefined ? {} : { label }),
	};
	return validateSeedBackup(backup);
}
export function generateSeedBackup(
	createdAt: number,
	label?: string
): SeedBackup {
	const entropy = PrivateKey.fromRandom().toArray("be", 32);
	return createSeedBackup(
		Mnemonic.fromEntropy(entropy).toString(),
		createdAt,
		label
	);
}
/** The package owns the shape; Sigma verifies recovery words and exact derived IDs. */
export function validateSeedBackup(value: unknown): SeedBackup {
	if (!isSigmaSeedBackup(value)) {
		throw new Error("Invalid or unsupported seed backup");
	}
	const { mnemonic } = mnemonicData(value.mnemonic);
	const hd = HD.fromSeed(mnemonic.toSeed(""));
	for (const profile of value.profiles) {
		if (profile.bapId !== deriveProfileFromHD(hd, profile.index).bapId) {
			throw new Error("Profile does not match recovery words");
		}
	}
	return value;
}
/** Pure append: durably encrypt the result before reporting profile creation success. */
export function appendSeedProfile(value: SeedBackup): {
	backup: SeedBackup;
	profile: ReturnType<typeof deriveSeedProfile>;
} {
	const backup = validateSeedBackup(value);
	if (backup.inventoryComplete === false) {
		throw new Error(
			"Import your complete backup file before creating another profile."
		);
	}
	if (backup.nextProfileIndex > MAX_INDEX) {
		throw new Error("Profile index space exhausted");
	}
	const profile = deriveSeedProfile(backup.mnemonic, backup.nextProfileIndex);
	return {
		profile,
		backup: {
			...backup,
			profiles: [
				...backup.profiles,
				{ index: profile.index, bapId: profile.bapId },
			],
			nextProfileIndex: backup.nextProfileIndex + 1,
		},
	};
}
